import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { ConfigContext } from '../../contexts/config-context';
import { UserContext } from '../../contexts/userContext';
import ListingAPI from '../../axios/listing';
import { formatNumber, LISTING_ROUTES, PRICE_UNITS } from '../main-page/home-utils';
import { MessageModal } from '../search-page/components/message-modal';
import { ContactPanel } from './components/contact-panel';
import { Gallery } from './components/gallery';
import { LoanCalculator } from './components/loan-calculator';
import { LocationSection } from './components/location-section';
import { ReportModal } from './components/report-modal';
import { ShareMenu } from './components/share-menu';
import { SimilarListings } from './components/similar-listings';
import "../../hs-theme.css";
import "./listing-detail.css";

// descriptions longer than this start collapsed
const LONG_DESCRIPTION = 420;
// listing flags shown as yes/no rows instead of chips
const SPEC_FLAGS = ['registered', 'vat', 'mortgage'];
// the sticky site header, which the price bar sits under
const HEADER_HEIGHT = 73;

const FACT_ICONS = {
    rooms: <><path d="M4 21h16" /><path d="M6 21V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17" /><path d="M14 12h.01" /></>,
    area: <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M8 16 16 8M12 8h4v4M12 16H8v-4" /></>,
    baths: <><path d="M4 12h16v3a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5v-3Z" /><path d="M6 12V5a2 2 0 0 1 3.5-1.3" /><path d="M7 20l-1 2M17 20l1 2" /></>,
    floor: <><path d="M3 21h4v-4h4v-4h4V9h4V5h2" /><path d="M3 21V3" /></>,
};

const Check = ({ size, width }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
);

export const ListingDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation(["details", "listing", "home"]);
    const { searchData, num_rooms_options, num_bathrooms_options, floor_options } = useContext(ConfigContext);
    const { user } = useContext(UserContext);
    const lang = i18n.language;

    const [listing, setListing] = useState(null);
    const [liked, setLiked] = useState(false);
    // users who saved the listing; kept apart so saving it here updates the number at once
    const [followers, setFollowers] = useState(0);
    const [descOpen, setDescOpen] = useState(false);
    const [messageOpen, setMessageOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    // phones and tablets: the price under the gallery, and whether it has scrolled up out of view
    const [priceEl, setPriceEl] = useState(null);
    const [priceBar, setPriceBar] = useState(false);

    // once the price has scrolled up under the header, a bar with it stays at the top
    useEffect(() => {
        if (!priceEl) return;
        const observer = new IntersectionObserver(([entry]) => {
            setPriceBar(!entry.isIntersecting && entry.boundingClientRect.top < HEADER_HEIGHT);
        }, { rootMargin: `-${HEADER_HEIGHT}px 0px 0px 0px` });
        observer.observe(priceEl);
        return () => observer.disconnect();
    }, [priceEl]);

    useEffect(() => {
        let cancelled = false;
        setListing(null);
        setDescOpen(false);
        window.scrollTo(0, 0);
        ListingAPI.getListingDetails(id, lang)
            .then(res => {
                if (cancelled) return;
                setListing(res.data);
                setFollowers(res.data.followers || 0);
            })
            .catch(err => {
                console.log(err);
                // the server answers a missing listing with 500 rather than 404
                if (!cancelled && err.response) navigate('/nepostojeci-oglas');
            });
        return () => { cancelled = true; };
    }, [id, lang]);

    useEffect(() => {
        setLiked(false);
        if (user) {
            ListingAPI.isListingLikedByUser(id).then(res => setLiked(res.data.is_liked)).catch(() => { });
        }
    }, [id, user]);

    if (!listing) return <div className='hs hs-detail'></div>;

    const typeId = listing.listing_type?.id || 1;
    const route = LISTING_ROUTES[typeId - 1];
    const placeSearch = { data: { ...searchData, listing_type: String(typeId), place: listing.place } };
    const price = parseFloat(listing.price) || 0;
    const area = parseFloat(listing.area) || 0;
    const bills = parseFloat(listing.bills) || 0;
    const deposit = parseFloat(listing.deposit) || 0;
    const properties = listing.properties || [];
    const rooms = num_rooms_options.find(o => o.id === listing.num_rooms)?.label;
    const baths = num_bathrooms_options.find(o => o.id === listing.num_bathrooms)?.label;
    const floorLabel = floor_options.find(o => o.id === listing.floor)?.label;
    const floor = floorLabel && (/^\d+$/.test(floorLabel) ? floorLabel : t(floorLabel, { ns: 'listing' }));
    const yesNo = (flag) => t(properties.includes(flag) ? 'yes' : 'no');
    const priceLabel = price > 0
        ? `${formatNumber(price)} €${typeId > 1 ? ` ${t(PRICE_UNITS[typeId - 1], { ns: 'home' })}` : ''}`
        : t('price_placeholder', { ns: 'listing' });
    const pricePerM2 = typeId === 1 && price > 0 && area > 0 ? `(${formatNumber(price / area)} €/m²)` : null;
    const description = (listing.description || '').trim();
    const longDescription = description.length > LONG_DESCRIPTION;

    const facts = [
        rooms && { icon: 'rooms', value: rooms, label: t('fact_rooms') },
        area > 0 && { icon: 'area', value: `${formatNumber(area)} m²`, label: t('fact_area') },
        baths && { icon: 'baths', value: baths, label: t('fact_baths') },
        floor && { icon: 'floor', value: listing.max_floor ? `${floor} / ${listing.max_floor}` : floor, label: t('fact_floor') },
    ].filter(Boolean);

    const specs = [
        [t('type', { ns: 'listing' }), listing.property_type?.label],
        [t('state', { ns: 'listing' }), listing.property_state?.label],
        [t('heating', { ns: 'listing' }), (listing.heating || []).map(h => h.name).join(', ')],
        [t('publisher', { ns: 'listing' }), listing.user_type?.label],
        [t('num_floors', { ns: 'listing' }), listing.max_floor],
        [t('registered', { ns: 'listing' }), yesNo('registered')],
        [t('vat', { ns: 'listing' }), yesNo('vat')],
        [t('mortgage', { ns: 'listing' }), yesNo('mortgage')],
        [t('bills', { ns: 'listing' }), bills > 0 && `${formatNumber(bills)} RSD`],
        [t('deposit', { ns: 'listing' }), deposit > 0 && `${formatNumber(deposit)} RSD`],
        [t('price_per_m2', { ns: 'listing' }), typeId === 1 && price > 0 && area > 0 && `${formatNumber(price / area)} €`],
    ].filter(([, value]) => value);

    const chips = [...properties.filter(flag => !SPEC_FLAGS.includes(flag)), ...(listing.ammenities || [])];

    const goBack = () => {
        // history.state.idx is 0 when the listing was opened directly
        if (window.history.state?.idx > 0) navigate(-1);
        else navigate(route);
    };

    const toggleLike = () => {
        if (!user) {
            navigate('/login');
            return;
        }
        const request = liked ? ListingAPI.unlikeListing(id) : ListingAPI.likeListing(id);
        request.then(() => {
            setLiked(!liked);
            setFollowers(count => Math.max(0, count + (liked ? -1 : 1)));
        }).catch(() => { });
    };

    // to the top of the contact card: price, publisher, call and message buttons and the viewing booking
    const goToContact = () => document.querySelector('.hs-contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const openMessage = () => {
        if (!user) {
            navigate('/login');
            return;
        }
        setMessageOpen(true);
    };

    // only logged-in users may report, so every report has a known sender
    const openReport = () => {
        if (!user) {
            navigate('/login');
            return;
        }
        setReportOpen(true);
    };

    // shown in the top row and again in the price bar
    const likeButton = (
        <button className={`hs-det-like ${liked ? 'on' : ''}`} onClick={toggleLike} aria-label={liked ? t('saved') : t('save')} aria-pressed={liked}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill={liked ? '#FF5722' : 'none'} stroke={liked ? '#FF5722' : '#0E1A2B'} strokeWidth="2"><path d="M12 20.5s-7.5-4.6-9.3-9.3C1.4 7.8 3.6 4.5 7 4.5c2 0 3.6 1.1 5 3 1.4-1.9 3-3 5-3 3.4 0 5.6 3.3 4.3 6.7-1.8 4.7-9.3 9.3-9.3 9.3Z" /></svg>
            <span className='hs-det-tool-label'>{liked ? t('saved') : t('save')}</span>
            {followers > 0 && <span className='hs-det-like-count' title={t('followers', { n: followers })}>{followers}</span>}
        </button>
    );

    return (
        <div className='hs hs-detail'>
            <main className='hs-detail-main'>
                <div className='hs-det-top'>
                    <nav className='hs-det-crumbs'>
                        <button className='hs-det-back' onClick={goBack} aria-label={t('back')}>
                            <span className='hs-det-back-arrow' aria-hidden='true'>←</span>
                            <span className='hs-det-back-label'>{t('back')}</span>
                        </button>
                        <Link to='/'>{t('home')}</Link><span>/</span>
                        <Link to={route}>{listing.listing_type?.label}</Link><span>/</span>
                        <Link to={route} state={placeSearch}>{listing.place?.label}</Link>
                        {listing.subplace?.label && <><span>/</span><span className='current'>{listing.subplace.label}</span></>}
                    </nav>
                    <div className='hs-det-tools'>
                        <ShareMenu listing={listing} />
                        <button className='hs-det-report' onClick={openReport} aria-label={t('report_listing')} title={t('report_listing')}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 21V4" /><path d="M5 4h11l-2 4 2 4H5" /></svg>
                            <span className='hs-det-tool-label'>{t('report_listing')}</span>
                        </button>
                        {likeButton}
                    </div>
                </div>

                <Gallery listing={listing} />

                <div className='hs-det-layout'>
                    <div className='hs-det-content'>
                        <section className='hs-det-section hs-det-basic'>
                            {/* the state and registration are in the characteristics; only an urgent sale is called out */}
                            {properties.includes('urgent') &&
                                <div className='hs-det-badges'><span className='hot'>{t('urgent', { ns: 'listing' })}</span></div>}
                            {/* phones and tablets, where the contact panel with the price drops below the content */}
                            <div ref={setPriceEl} className='hs-det-mprice'>
                                <span className='hs-det-mprice-value'>{priceLabel}</span>
                                {pricePerM2 && <span className='hs-det-mprice-sub'>{pricePerM2}</span>}
                            </div>
                            <h1>{listing.title}</h1>
                            <p className='hs-det-loc'>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21Z" /><circle cx="12" cy="9.5" r="2.5" /></svg>
                                <span>
                                    {[listing.place?.label, listing.subplace?.label, listing.location?.label, listing.address].filter(Boolean).join(' · ')}
                                    <a href='#lokacija'>{t('show_on_map')}</a>
                                </span>
                            </p>
                            <div className='hs-det-facts'>
                                {facts.map(fact => (
                                    <div key={fact.icon} className='hs-det-fact'>
                                        <span className='hs-det-fact-icon'>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{FACT_ICONS[fact.icon]}</svg>
                                        </span>
                                        <span className='hs-det-fact-text'>
                                            <span className='hs-det-fact-value'>{fact.value}</span>
                                            <span className='hs-det-fact-label'>{fact.label}</span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {description &&
                            <section className='hs-det-section hs-det-desc-card'>
                                <h2>{t('description_title')}</h2>
                                <div className={`hs-det-desc ${longDescription && !descOpen ? 'collapsed' : ''}`}>
                                    {/* blank lines in the text split it into paragraphs */}
                                    {description.split(/\n\s*\n/).map((paragraph, i) => <p key={i}>{paragraph.trim()}</p>)}
                                </div>
                                {longDescription &&
                                    <button className='hs-det-desc-toggle' onClick={() => setDescOpen(!descOpen)}>
                                        {descOpen ? t('show_less') : t('show_more')}
                                    </button>}
                            </section>}

                        <section className='hs-det-section'>
                            <h2>{t('characteristics')}</h2>
                            <div className='hs-det-specs'>
                                {specs.map(([label, value]) => (
                                    <div key={label}><span>{label}</span><strong>{value}</strong></div>
                                ))}
                            </div>
                            {chips.length > 0 &&
                                <div className='hs-det-amenities'>
                                    <h3>{t('amenities')}</h3>
                                    <div className='hs-det-chips'>
                                        {chips.map(chip => (
                                            <span key={chip}><span className='hs-det-chip-check'><Check size={11} width={3.2} /></span>{t(chip, { ns: 'listing' })}</span>
                                        ))}
                                    </div>
                                </div>}
                        </section>

                        <LocationSection listing={listing} />

                        {typeId === 1 && price > 0 && <LoanCalculator price={price} />}
                    </div>

                    <ContactPanel listing={listing} onMessage={openMessage} />
                </div>

                <SimilarListings listing={listing} />
            </main>

            <div className={`hs-det-pricebar ${priceBar ? 'on' : ''}`} aria-hidden={!priceBar}>
                <div className='hs-det-pricebar-text'>
                    <span className='hs-det-pricebar-price'>{priceLabel}</span>
                    {pricePerM2 && <span className='hs-det-pricebar-sub'>{pricePerM2}</span>}
                </div>
                <div className='hs-det-tools hs-det-pricebar-tools'>
                    <ShareMenu listing={listing} />
                    {likeButton}
                    <button onClick={goToContact} aria-label={t('contact')} title={t('contact')}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" /></svg>
                    </button>
                </div>
            </div>

            {messageOpen && <MessageModal listing={listing} onClose={() => setMessageOpen(false)} />}
            {reportOpen && <ReportModal listing={listing} onClose={() => setReportOpen(false)} />}
        </div>
    );
};
